/**
 * Exact decimal helpers for leave balances (client port of backend/utils/decimal.utils.js).
 *
 * Admin screens display and calculate with the full decimal number; only
 * user-facing screens round to 4 decimal places. Values are converted to exact
 * integer units of 10^-14 hours so no binary-float rounding enters the math.
 *
 * Note: written without BigInt literals / exponent operator so it compiles
 * under the project's ES6 target.
 */

const SCALE_DIGITS = 14;
const SCALE = BigInt(`1${"0".repeat(SCALE_DIGITS)}`); // 10^14 units per hour
const ZERO = BigInt(0);

/** Values the backend accepts for rates/adjustments: up to 14 decimal places. */
export const DECIMAL_INPUT_PATTERN = /^-?\d+(\.\d{1,14})?$/;

const toUnits = (value: string | number): bigint => {
  if (value === null || value === undefined || value === "") return ZERO;

  let s = String(value).trim();
  if (/e/i.test(s)) s = Number(s).toFixed(SCALE_DIGITS);

  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(s);
  if (!match) return ZERO; // lenient: invalid input is validated before submit

  const [, sign, intPart, fracPart = ""] = match;
  const frac = (fracPart + "0".repeat(SCALE_DIGITS)).slice(0, SCALE_DIGITS);
  const units = BigInt(intPart) * SCALE + BigInt(frac);
  return sign === "-" ? -units : units;
};

const formatUnits = (units: bigint): string => {
  const neg = units < ZERO;
  const abs = neg ? -units : units;
  const intPart = abs / SCALE;
  const fracPart = (abs % SCALE)
    .toString()
    .padStart(SCALE_DIGITS, "0")
    .replace(/0+$/, "");
  return (neg ? "-" : "") + (fracPart ? `${intPart}.${fracPart}` : intPart.toString());
};

/** Format a decimal value exactly, trimming trailing zeros (e.g. "3.07690000000000" -> "3.0769"). */
export const formatDecimal = (value: string | number): string => formatUnits(toUnits(value));

/** Add two decimal values exactly; returns the exact decimal string. */
export const addDecimals = (a: string | number, b: string | number): string =>
  formatUnits(toUnits(a) + toUnits(b));
